import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Trash2, Eye, RefreshCw, Mail, Clock, Calendar, BarChart3, CheckCircle, XCircle, Star, ArrowRight, Activity } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BrowseAutomationTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [abStats, setAbStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState('');
  const [activeSection, setActiveSection] = useState('sessions');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [browseRes, abRes] = await Promise.all([
        axios.get(`${API_URL}/admin/browse-sessions/stats`, { headers }),
        axios.get(`${API_URL}/admin/ab-tests/stats`, { headers }),
      ]);
      setStats(browseRes.data);
      setAbStats(abRes.data);
    } catch (error) {
      toast.error('Failed to load automation stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const clearSessions = async () => {
    if (!window.confirm('Clear all browse sessions? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/admin/browse-sessions/clear`, { headers });
      toast.success('All sessions cleared');
      fetchStats();
    } catch { toast.error('Failed to clear sessions'); }
  };

  const deleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API_URL}/admin/browse-sessions/${sessionId}`, { headers });
      toast.success('Session deleted');
      fetchStats();
    } catch { toast.error('Failed to delete session'); }
  };

  const previewEmail = async (sessionId) => {
    try {
      const res = await axios.get(`${API_URL}/admin/browse-sessions/${sessionId}/preview-email`, { headers });
      setPreviewHtml(res.data.html);
      setPreviewSubject(res.data.subject);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview email');
    }
  };

  const triggerJob = async (jobId) => {
    try {
      await axios.post(`${API_URL}/admin/scheduler/run/${jobId}`, {}, { headers });
      toast.success(`Job "${jobId}" triggered`);
      setTimeout(fetchStats, 3000);
    } catch { toast.error('Failed to trigger job'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="browse-automation-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Browse Automation & A/B Testing</h2>
        <Button variant="outline" onClick={fetchStats} data-testid="refresh-automation-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Section Toggle */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'sessions', label: 'Browse Sessions', icon: Activity },
          { id: 'ab', label: 'A/B Testing', icon: BarChart3 },
          { id: 'jobs', label: 'Automation Jobs', icon: Clock },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveSection(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t text-sm font-medium transition-colors ${
              activeSection === id ? 'bg-[#0A2342] text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`section-${id}-btn`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* Browse Sessions Section */}
      {activeSection === 'sessions' && stats && (
        <div className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="session-stats">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-700">{stats.total_sessions}</div>
              <div className="text-sm text-blue-600">Total Sessions</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-700">{stats.active_sessions}</div>
              <div className="text-sm text-yellow-600">Active</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-700">{stats.emailed}</div>
              <div className="text-sm text-green-600">Emailed</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-700">{stats.queue_pending}</div>
              <div className="text-sm text-purple-600">Queue Pending</div>
            </div>
            <div className={`${stats.is_payday_window ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'} border rounded-lg p-4 text-center`}>
              <div className="text-2xl font-bold">{stats.is_payday_window ? 'YES' : 'NO'}</div>
              <div className="text-sm text-gray-600">Payday Window</div>
              {!stats.is_payday_window && (
                <div className="text-xs text-gray-400 mt-1">
                  Next: {new Date(stats.next_payday_window).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button onClick={() => triggerJob('abandoned_browse')} className="bg-blue-600 hover:bg-blue-700" data-testid="trigger-browse-btn">
              <Mail className="w-4 h-4 mr-2" /> Run Abandoned Browse Check
            </Button>
            <Button onClick={() => triggerJob('email_queue')} variant="outline" data-testid="trigger-queue-btn">
              <Clock className="w-4 h-4 mr-2" /> Process Email Queue
            </Button>
            <Button onClick={clearSessions} variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" data-testid="clear-sessions-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Clear All Sessions
            </Button>
          </div>

          {/* Recent Sessions Table */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h4 className="font-semibold text-sm">Recent Browse Sessions ({stats.recent_sessions?.length || 0})</h4>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {stats.recent_sessions?.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No browse sessions yet</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left p-3">User</th>
                      <th className="text-left p-3">Products Viewed</th>
                      <th className="text-center p-3">Count</th>
                      <th className="text-center p-3">Status</th>
                      <th className="text-left p-3">Last Activity</th>
                      <th className="text-center p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_sessions?.map((session, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{session.user_email}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {session.products?.map((name, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{name}</Badge>
                            ))}
                          </div>
                        </td>
                        <td className="p-3 text-center">{session.product_count}</td>
                        <td className="p-3 text-center">
                          {session.email_sent ? (
                            <Badge className="bg-green-100 text-green-700">Emailed</Badge>
                          ) : session.purchased ? (
                            <Badge className="bg-blue-100 text-blue-700">Purchased</Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-700">Active</Badge>
                          )}
                        </td>
                        <td className="p-3 text-gray-500 text-xs">
                          {new Date(session.last_activity).toLocaleString('en-GB')}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => previewEmail(session.session_id)} className="p-1.5 rounded hover:bg-blue-50" title="Preview email">
                              <Eye className="w-4 h-4 text-blue-600" />
                            </button>
                            <button onClick={() => deleteSession(session.session_id)} className="p-1.5 rounded hover:bg-red-50" title="Delete session">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Email Preview Modal */}
          {previewHtml && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreviewHtml(null)}>
              <div className="bg-white rounded-lg max-w-[650px] w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                  <div>
                    <h3 className="font-semibold">Email Preview</h3>
                    <p className="text-sm text-gray-500">Subject: {previewSubject}</p>
                  </div>
                  <button onClick={() => setPreviewHtml(null)} className="p-2 hover:bg-gray-100 rounded">
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
                <div className="overflow-y-auto max-h-[75vh]">
                  <iframe
                    srcDoc={previewHtml}
                    title="Email Preview"
                    className="w-full border-0"
                    style={{ height: '800px' }}
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* A/B Testing Section */}
      {activeSection === 'ab' && abStats && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            A/B testing tracks CTA wording, subject lines, and send timing. Variants are randomly assigned per email. Statistical significance is calculated using chi-squared tests.
          </p>

          {/* Statistical Significance Banner */}
          {abStats.cta_significance && abStats.cta_significance.significant && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-testid="significance-banner">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-green-800">CTA Winner Declared!</p>
                <p className="text-sm text-green-700">
                  Variant {abStats.cta_significance.winner?.toUpperCase()} wins with {abStats.cta_significance.confidence}% confidence (p={abStats.cta_significance.p_value})
                </p>
              </div>
            </div>
          )}
          {abStats.subject_significance && abStats.subject_significance.significant && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-blue-800">Subject Line Winner Declared!</p>
                <p className="text-sm text-blue-700">
                  Variant {abStats.subject_significance.winner?.toUpperCase()} wins with {abStats.subject_significance.confidence}% confidence (p={abStats.subject_significance.p_value})
                </p>
              </div>
            </div>
          )}

          {/* CTA Variants */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
              <h4 className="font-semibold text-sm">CTA Wording Variants</h4>
              {abStats.cta_significance && !abStats.cta_significance.significant && abStats.cta_significance.sample_needed > 0 && (
                <span className="text-xs text-gray-500">~{abStats.cta_significance.sample_needed} more sends needed for significance</span>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3">Variant</th>
                  <th className="text-left p-3">CTA Text</th>
                  <th className="text-center p-3">Sent</th>
                  <th className="text-center p-3">Opened</th>
                  <th className="text-center p-3">Clicked</th>
                  <th className="text-center p-3">Converted</th>
                  <th className="text-center p-3">Click Rate</th>
                  <th className="text-center p-3">Conv Rate</th>
                </tr>
              </thead>
              <tbody>
                {abStats.cta_stats?.map((v, idx) => (
                  <tr key={idx} className={`border-b hover:bg-gray-50 ${abStats.cta_significance?.significant && abStats.cta_significance?.winner === v.variant_id ? 'bg-green-50' : ''}`}>
                    <td className="p-3">
                      <Badge variant="outline" className="font-mono">{v.variant_id.toUpperCase()}</Badge>
                      {abStats.cta_significance?.significant && abStats.cta_significance?.winner === v.variant_id && (
                        <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 inline ml-1" />
                      )}
                    </td>
                    <td className="p-3 font-medium">{v.primary_cta}</td>
                    <td className="p-3 text-center">{v.sent}</td>
                    <td className="p-3 text-center">{v.opened}</td>
                    <td className="p-3 text-center">{v.clicked}</td>
                    <td className="p-3 text-center">{v.converted}</td>
                    <td className="p-3 text-center">
                      <span className={`font-semibold ${v.click_rate > 0 ? 'text-green-600' : 'text-gray-400'}`}>{v.click_rate}%</span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`font-semibold ${v.conversion_rate > 0 ? 'text-blue-600' : 'text-gray-400'}`}>{v.conversion_rate}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {abStats.total_events === 0 && (
              <div className="p-6 text-center text-gray-500 text-sm">
                No A/B test data yet. Variants are assigned when abandoned browse emails are sent.
              </div>
            )}
          </div>

          {/* Subject Line Variants */}
          <div className="bg-white rounded-lg border overflow-hidden" data-testid="subject-variants-table">
            <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
              <h4 className="font-semibold text-sm">Subject Line Variants</h4>
              {abStats.subject_significance && !abStats.subject_significance.significant && abStats.subject_significance.sample_needed > 0 && (
                <span className="text-xs text-gray-500">~{abStats.subject_significance.sample_needed} more sends needed</span>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3">Variant</th>
                  <th className="text-left p-3">Subject Template</th>
                  <th className="text-center p-3">Sent</th>
                  <th className="text-center p-3">Opened</th>
                  <th className="text-center p-3">Open Rate</th>
                </tr>
              </thead>
              <tbody>
                {abStats.subject_stats?.map((v, idx) => (
                  <tr key={idx} className={`border-b hover:bg-gray-50 ${abStats.subject_significance?.significant && abStats.subject_significance?.winner === v.variant_id ? 'bg-blue-50' : ''}`}>
                    <td className="p-3">
                      <Badge variant="outline" className="font-mono">{v.variant_id.toUpperCase()}</Badge>
                      {abStats.subject_significance?.significant && abStats.subject_significance?.winner === v.variant_id && (
                        <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 inline ml-1" />
                      )}
                    </td>
                    <td className="p-3 font-medium text-gray-700">{v.template}</td>
                    <td className="p-3 text-center">{v.sent}</td>
                    <td className="p-3 text-center">{v.opened}</td>
                    <td className="p-3 text-center">
                      <span className={`font-semibold ${v.open_rate > 0 ? 'text-green-600' : 'text-gray-400'}`}>{v.open_rate}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Campaign History */}
          {abStats.campaigns?.length > 0 && (
            <div className="bg-white rounded-lg border overflow-hidden" data-testid="campaign-history">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-semibold text-sm">Campaign History (Last 14 Days)</h4>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3">Date</th>
                    <th className="text-center p-3">Sent</th>
                    <th className="text-center p-3">Opened</th>
                    <th className="text-center p-3">Clicked</th>
                    <th className="text-center p-3">Converted</th>
                    <th className="text-center p-3">Open Rate</th>
                    <th className="text-center p-3">Click Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {abStats.campaigns.map((c, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{new Date(c.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</td>
                      <td className="p-3 text-center">{c.sent}</td>
                      <td className="p-3 text-center">{c.opened}</td>
                      <td className="p-3 text-center">{c.clicked}</td>
                      <td className="p-3 text-center">{c.converted}</td>
                      <td className="p-3 text-center"><span className="text-green-600 font-semibold">{c.open_rate}%</span></td>
                      <td className="p-3 text-center"><span className="text-blue-600 font-semibold">{c.click_rate}%</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Timing Variants */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h4 className="font-semibold text-sm">Send Timing Variants</h4>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3">Timing</th>
                  <th className="text-center p-3">Delay</th>
                  <th className="text-center p-3">Sent</th>
                  <th className="text-center p-3">Clicked</th>
                  <th className="text-center p-3">Click Rate</th>
                </tr>
              </thead>
              <tbody>
                {abStats.timing_stats?.map((v, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{v.label}</td>
                    <td className="p-3 text-center">{v.delay_minutes} min</td>
                    <td className="p-3 text-center">{v.sent}</td>
                    <td className="p-3 text-center">{v.clicked}</td>
                    <td className="p-3 text-center">
                      <span className={`font-semibold ${v.click_rate > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {v.click_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Automation Jobs Section */}
      {activeSection === 'jobs' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Manage automated jobs for browse emails, featured rotation, and email queue processing.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { id: 'abandoned_browse', name: 'Abandoned Browse', desc: 'Check for inactive sessions & send emails', schedule: 'Every 30 min', color: 'blue' },
              { id: 'featured_rotation', name: 'Featured Rotation', desc: 'Rotate featured products (5 per section)', schedule: 'Daily 00:05 SAST', color: 'purple' },
              { id: 'email_queue', name: 'Email Queue', desc: 'Process follow-up & payday reminder emails', schedule: 'Every 60 min', color: 'green' },
            ].map(job => (
              <div key={job.id} className={`bg-${job.color}-50 border border-${job.color}-200 rounded-lg p-4`}>
                <h4 className={`font-semibold text-${job.color}-800 mb-1`}>{job.name}</h4>
                <p className="text-sm text-gray-600 mb-2">{job.desc}</p>
                <p className="text-xs text-gray-500 mb-3">
                  <Clock className="w-3 h-3 inline mr-1" />{job.schedule}
                </p>
                <Button
                  size="sm"
                  onClick={() => triggerJob(job.id)}
                  className={`bg-${job.color}-600 hover:bg-${job.color}-700`}
                  data-testid={`trigger-${job.id}-btn`}
                >
                  <ArrowRight className="w-3 h-3 mr-1" /> Run Now
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrowseAutomationTab;
